import { Link } from '@tanstack/react-router';
import { ChevronRight, MessageCircle, Users, Video, type LucideIcon } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

/**
 * « Besoin d'aide ? » — 3 raccourcis du tableau de bord élève vers des pages
 * EXISTANTES (aucune logique propre) : rendez-vous, discussions, mon réseau.
 */
interface HelpShortcut {
  id: string;
  title: string;
  subtitle: string;
  to: '/student/rendez-vous' | '/student/discussions' | '/student/contacts';
  icon: LucideIcon;
  card: string;
  tile: string;
}

export const STUDENT_HELP_SHORTCUTS: readonly HelpShortcut[] = [
  {
    id: 'express',
    title: 'Cours express',
    subtitle: 'Un prof disponible en quelques minutes',
    to: '/student/rendez-vous',
    icon: Video,
    card: 'from-[#fff1f1] to-[#ffe4e6] border-[#fecdd3] ocean:from-[#3a1430] ocean:to-[#2a1030] ocean:border-[#7a2a48]',
    tile: 'bg-[linear-gradient(180deg,#fb7185,#ef4444)] shadow-[0_6px_14px_rgba(239,68,68,0.3)]',
  },
  {
    id: 'question',
    title: 'Poser une question',
    subtitle: 'Par messagerie',
    to: '/student/discussions',
    icon: MessageCircle,
    card: 'from-[#eef5ff] to-[#dbeafe] border-[#c7dcfd] ocean:from-[#0f2c66] ocean:to-[#0b2152] ocean:border-[#2a4f9e]',
    tile: 'bg-[linear-gradient(180deg,#3b8bff,#0566f2)] shadow-[0_6px_14px_rgba(5,102,242,0.3)]',
  },
  {
    id: 'tutor',
    title: 'Mon tuteur',
    subtitle: 'Suivi régulier et objectifs',
    to: '/student/contacts',
    icon: Users,
    card: 'from-[#ecfdf5] to-[#d1fae5] border-[#bbf7d0] ocean:from-[#0c3a33] ocean:to-[#0a2c33] ocean:border-[#1f6b55]',
    tile: 'bg-[linear-gradient(180deg,#34d399,#10b981)] shadow-[0_6px_14px_rgba(16,185,129,0.3)]',
  },
] as const;

export function StudentHelpShortcuts() {
  return (
    <section className="space-y-3" aria-labelledby="student-help-title">
      <h2 id="student-help-title" className="font-display text-ink-900 text-lg font-bold">
        Besoin d’aide&nbsp;?
      </h2>
      {/* Compact : 3 tuiles sur une ligne dès 375 px (sous-titre à partir de sm). */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {STUDENT_HELP_SHORTCUTS.map(({ id, title, subtitle, to, icon: Icon, card, tile }) => (
          <Link
            key={id}
            to={to}
            className={cn(
              'flex min-h-[2.75rem] flex-col items-center gap-2 rounded-2xl border bg-gradient-to-br p-3 text-center transition-transform active:scale-[0.98] sm:flex-row sm:p-4 sm:text-left',
              card,
            )}
          >
            <span
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white sm:h-12 sm:w-12 sm:rounded-2xl',
                tile,
              )}
              aria-hidden="true"
            >
              <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-display text-ink-900 block text-sm font-bold leading-tight sm:text-[15px]">
                {title}
              </span>
              <span className="text-ink-500 mt-0.5 hidden text-sm leading-snug sm:block">
                {subtitle}
              </span>
            </span>
            <ChevronRight
              className="text-ink-400 hidden h-5 w-5 shrink-0 sm:block"
              aria-hidden="true"
            />
          </Link>
        ))}
      </div>
    </section>
  );
}
